// Binance spot symbols for each ticker (USDT pairs)
export const BINANCE_SYMBOLS = {
  TRX:  'TRXUSDT',
  SOL:  'SOLUSDT',
  ETH:  'ETHUSDT',
  BTC:  'BTCUSDT',
  BNB:  'BNBUSDT',
  MATIC:'MATICUSDT',
  LINK: 'LINKUSDT',
  UNI:  'UNIUSDT',
  SHIB: 'SHIBUSDT',
  PEPE: 'PEPEUSDT',
  WIN:  'WINUSDT',
  BTT:  'BTTCUSDT',
  CAKE: 'CAKEUSDT',
  WBTC: 'WBTCUSDT',
  WETH: 'WETHUSDT',
  JST:  'JSTUSDT',
  SUN:  'SUNUSDT',
  // Rest of the BSC/ETH/Polygon default token lists — Binance has USDT pairs
  // for all of these, so routing token pricing through here first (one
  // batched request, no per-address limit) avoids CoinGecko's contract-price
  // endpoint entirely for anything but truly obscure/custom-discovered tokens.
  DOGE: 'DOGEUSDT', DOT: 'DOTUSDT', ADA: 'ADAUSDT', XRP: 'XRPUSDT', LTC: 'LTCUSDT',
  AVAX: 'AVAXUSDT', ATOM: 'ATOMUSDT', AAVE: 'AAVEUSDT', BCH: 'BCHUSDT', ETC: 'ETCUSDT',
  FTM: 'FTMUSDT', AXS: 'AXSUSDT', SUSHI: 'SUSHIUSDT', SNX: 'SNXUSDT', MKR: 'MKRUSDT',
  MANA: 'MANAUSDT', SAND: 'SANDUSDT', BAT: 'BATUSDT', VET: 'VETUSDT', XLM: 'XLMUSDT',
  EOS: 'EOSUSDT', ZEC: 'ZECUSDT', FIL: 'FILUSDT', XTZ: 'XTZUSDT', CELR: 'CELRUSDT',
  ONE: 'ONEUSDT', GMT: 'GMTUSDT', FLOKI: 'FLOKIUSDT', TON: 'TONUSDT', IOTA: 'IOTAUSDT',
  ZIL: 'ZILUSDT',
  // Wrapped-token tickers price the same as their underlying asset but don't
  // match the bare ticker above (WBNB !== BNB as a map key) — found by
  // auditing every ticker in the BSC/ETH/Polygon/Tron default token lists
  // against this map. Also fixes BTTC (Tron's ticker) not matching the
  // existing BTT entry.
  WBNB: 'BNBUSDT', WMATIC: 'MATICUSDT', BTTC: 'BTTCUSDT',
  // Solana default token list
  RAY: 'RAYUSDT', BONK: 'BONKUSDT', JTO: 'JTOUSDT', WIF: 'WIFUSDT',
  // Added to the BSC default list on request
  INJ: 'INJUSDT', NEAR: 'NEARUSDT',
};

// Stablecoins — always $1 without an API call
const STABLES = new Set(['USDT', 'USDC', 'BUSD', 'DAI', 'USDD', 'TUSD', 'FDUSD', 'USDP']);

// CoinGecko IDs — used as fallback for tickers not on Binance
export const COINGECKO_IDS = {
  TRX:   'tron',
  SOL:   'solana',
  ETH:   'ethereum',
  BTC:   'bitcoin',
  BNB:   'binancecoin',
  MATIC: 'matic-network',
  USDT:  'tether',
  USDC:  'usd-coin',
  DAI:   'dai',
  WBTC:  'wrapped-bitcoin',
  LINK:  'chainlink',
  UNI:   'uniswap',
  SHIB:  'shiba-inu',
  PEPE:  'pepe',
  WIN:   'winklink',
  BTT:   'bittorrent',
  BTTC:  'bittorrent',
  CAKE:  'pancakeswap-token',
  BUSD:  'binance-usd',
  WETH:  'weth',
  JST:   'just',
  SUN:   'sun-token',
  USDD:  'usdd',
};

const CACHE_TTL = 60000;
let cache = { prices: {}, changes: {}, fetchedAt: 0 };

// ── Binance batch price fetch ─────────────────────────────────────────────────

async function fetchBinancePrices(tickers) {
  const needed = tickers.filter(t => BINANCE_SYMBOLS[t]);
  if (!needed.length) return {};

  const symbols = [...new Set(needed.map(t => BINANCE_SYMBOLS[t]))];
  const encoded = JSON.stringify(symbols);

  // Binance batch price: GET /api/v3/ticker/price?symbols=[...]
  const url = `https://api.binance.com/api/v3/ticker/price?symbols=${encodeURIComponent(encoded)}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
  if (!res.ok) throw new Error(`Binance HTTP ${res.status}`);
  const data = await res.json(); // [{ symbol: "BTCUSDT", price: "67000.00" }, ...]

  // Build reverse map: BTCUSDT → price
  const bySymbol = {};
  for (const item of data) {
    bySymbol[item.symbol] = parseFloat(item.price);
  }

  // Map back to tickers
  const prices = {};
  for (const t of needed) {
    const sym = BINANCE_SYMBOLS[t];
    if (bySymbol[sym] !== undefined) prices[t] = bySymbol[sym];
  }
  return prices;
}

// ── 24h change from Binance ───────────────────────────────────────────────────

async function fetchBinanceChanges(tickers) {
  const needed = tickers.filter(t => BINANCE_SYMBOLS[t]);
  if (!needed.length) return {};

  const symbols = [...new Set(needed.map(t => BINANCE_SYMBOLS[t]))];
  const encoded = JSON.stringify(symbols);
  const url = `https://api.binance.com/api/v3/ticker/24hr?symbols=${encodeURIComponent(encoded)}&type=MINI`;
  const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
  if (!res.ok) return {};
  const data = await res.json();

  const changes = {};
  const bySymbol = {};
  for (const item of data) bySymbol[item.symbol] = parseFloat(item.priceChangePercent);
  for (const t of needed) {
    const sym = BINANCE_SYMBOLS[t];
    if (bySymbol[sym] !== undefined) changes[t] = bySymbol[sym];
  }
  return changes;
}

// ── CoinGecko fallback ────────────────────────────────────────────────────────

async function fetchCoinGeckoPrices(tickers) {
  const ids = [...new Set(tickers.map(t => COINGECKO_IDS[t]).filter(Boolean))];
  if (!ids.length) return { prices: {}, changes: {} };

  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids.join(',')}&vs_currencies=usd&include_24hr_change=true`;
  const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
  const data = await res.json();

  const prices = {};
  const changes = {};
  for (const [ticker, id] of Object.entries(COINGECKO_IDS)) {
    if (data[id]) {
      prices[ticker] = data[id].usd;
      changes[ticker] = data[id].usd_24h_change ?? null;
    }
  }
  return { prices, changes };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Fetch USDT prices for the given tickers.
 * Primary: Binance API  (fast, no key, reliable)
 * Fallback: CoinGecko   (covers obscure tokens)
 * Stablecoins hardcoded to 1.0
 */
export async function fetchPrices(tickers, force = false) {
  const now = Date.now();
  const unique = [...new Set(tickers)];

  // The cache used to be keyed purely by *time* — any call within CACHE_TTL
  // short-circuited with the existing cache object regardless of which tickers it
  // actually held. Two pages calling fetchPrices() with different ticker sets
  // within 60s of each other meant the second page's tickers could just be
  // missing from what it got back (e.g. BSC's native BNB balance silently having
  // no price because an ETH page's fetch — for different tickers — populated the
  // cache moments earlier). Now only short-circuits when every requested ticker
  // is actually present.
  const cacheIsFresh = now - cache.fetchedAt < CACHE_TTL;
  const allCached = unique.every(t => cache.prices[t] !== undefined);
  if (!force && cacheIsFresh && allCached) return cache;

  // Build on top of the existing cache (so tickers other pages already fetched
  // aren't discarded) — fetch everything fresh on a forced refresh, otherwise
  // only the gaps.
  const prices = { ...cache.prices };
  const changes = { ...cache.changes };
  const toFetch = force ? unique : unique.filter(t => prices[t] === undefined);

  // 1. Stablecoins — instant
  for (const t of toFetch) {
    if (STABLES.has(t)) { prices[t] = 1.0; changes[t] = 0; }
  }

  // 2. Binance (primary) — all tickers that have a Binance symbol
  const binanceTickers = toFetch.filter(t => BINANCE_SYMBOLS[t]);
  if (binanceTickers.length) {
    try {
      const [bp, bc] = await Promise.all([
        fetchBinancePrices(binanceTickers),
        fetchBinanceChanges(binanceTickers),
      ]);
      Object.assign(prices, bp);
      Object.assign(changes, bc);
    } catch (e) {
      console.warn('Binance price fetch failed, falling back to CoinGecko:', e.message);
    }
  }

  // 3. CoinGecko fallback — tickers not yet priced (not on Binance or Binance failed)
  const unpricedTickers = toFetch.filter(t => prices[t] === undefined && !STABLES.has(t));
  if (unpricedTickers.length) {
    try {
      const { prices: cgp, changes: cgc } = await fetchCoinGeckoPrices(unpricedTickers);
      Object.assign(prices, cgp);
      Object.assign(changes, cgc);
    } catch (e) {
      console.warn('CoinGecko fallback failed:', e.message);
    }
  }

  if (Object.keys(prices).length > 0) {
    cache = { prices, changes, fetchedAt: now };
  }

  return cache;
}

export function usdValue(ticker, rawBalance) {
  const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
  const price = cache.prices[ticker] ?? 0;
  return raw * price;
}

export function formatUsd(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) return '—';
  if (amount >= 1000000) return '$' + (amount / 1000000).toFixed(2) + 'M';
  if (amount >= 1000) return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

export function getCachedPrice(ticker) {
  if (STABLES.has(ticker)) return 1.0;
  return cache.prices[ticker] ?? null;
}

export function getCachedPrices() {
  return cache.prices;
}

export function isCacheStale() {
  return Date.now() - cache.fetchedAt > CACHE_TTL;
}

// ── CoinGecko platform IDs (for contract-address token lookup) ────────────────
export const CHAIN_PLATFORM = {
  1:   'ethereum',
  56:  'binance-smart-chain',
  137: 'polygon-pos',
};

const contractPriceCache = {};
const CONTRACT_CACHE_TTL = 120000;

// CoinGecko's public (no-key) token_price endpoint hard-rejects any request with
// more than 1 contract address ("Number of contract addresses in the request
// exceeds the allowed limit of 1") — it used to accept large batches. A rejected
// batch returns a JSON error object, not a price map, and every address in that
// batch silently priced at $0 downstream. The anonymous rate limit is also much
// tighter than "just don't batch": firing even 4 requests concurrently gets
// every one past the first ~4 in the whole burst 429'd. Both limits lift with a
// free CoinGecko Demo API key (nodereal-style free signup, no payment) — up to
// 515 addresses per request and 100 calls/min — so a keyed request goes back to
// one/few batched calls; an unkeyed one stays fully serial with a delay between
// requests so it doesn't 429 its way through a long token list (which is
// exactly why coins later in an unkeyed list, e.g. SHIB/DOGE on BSC, were
// pricing at $0 even after the "1 address per request" fix).
const REQUEST_DELAY_MS = 1500;
const KEYED_BATCH = 100; // comfortably under the 515 max, keeps URL length sane
const sleep = ms => new Promise(r => setTimeout(r, ms));

async function fetchContractBatch(platform, addrs, apiKey) {
  const url = `https://api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${addrs.join(',')}&vs_currencies=usd`;
  const opts = { signal: AbortSignal.timeout(15000) };
  if (apiKey) opts.headers = { 'x-cg-demo-api-key': apiKey };
  const res = await fetch(url, opts);
  if (res.status === 429) return { rateLimited: true, data: null };
  return { rateLimited: false, data: await res.json() };
}

// onProgress(status), status = { stage, current, total, address?, usd? } — pages
// call this to show a live "Pricing 3/12 tokens via CoinGecko…" indicator, and
// can also use the per-item address/usd payload to render that one token's price
// into the UI immediately rather than waiting for the whole list to finish.
export async function fetchPricesByContract(platform, contractAddresses, onProgress) {
  if (!contractAddresses.length) return {};
  const now = Date.now();

  const toFetch = [];
  const result = {};
  for (const addr of contractAddresses) {
    const key = `${platform}:${addr.toLowerCase()}`;
    const cached = contractPriceCache[key];
    if (cached && now - cached.fetchedAt < CONTRACT_CACHE_TTL) {
      result[addr.toLowerCase()] = cached.usd;
    } else {
      toFetch.push(addr.toLowerCase());
    }
  }

  if (!toFetch.length) return result;

  const apiKey = process.env.VUE_APP_COINGECKO_KEY || '';

  if (apiKey) {
    // Keyed: batch KEYED_BATCH addresses per request, no artificial pacing —
    // 100 calls/min is generous enough that even a very long token list only
    // takes a handful of requests.
    let done = 0;
    for (let i = 0; i < toFetch.length; i += KEYED_BATCH) {
      const batch = toFetch.slice(i, i + KEYED_BATCH);
      try {
        const { rateLimited, data } = await fetchContractBatch(platform, batch, apiKey);
        if (rateLimited) {
          console.warn(`CoinGecko rate-limited after ${done}/${toFetch.length} contract price lookups (keyed)`);
          break;
        }
        for (const addr of batch) {
          const val = data && data[addr];
          const usd = val ? (val.usd || 0) : 0;
          if (val) {
            result[addr] = usd;
            contractPriceCache[`${platform}:${addr}`] = { usd, fetchedAt: now };
          }
          done++;
          if (onProgress) onProgress({ stage: 'coingecko', current: done, total: toFetch.length, address: addr, usd: val ? usd : null });
        }
      } catch (e) {
        console.warn(`CoinGecko batch price fetch failed:`, e.message);
        done += batch.length;
      }
    }
    return result;
  }

  // Unkeyed: fully serial, one address per request, paced to survive the tight
  // anonymous rate limit.
  for (let i = 0; i < toFetch.length; i++) {
    const addr = toFetch[i];
    if (onProgress) onProgress({ stage: 'coingecko', current: i + 1, total: toFetch.length });
    try {
      const { rateLimited, data } = await fetchContractBatch(platform, [addr], '');
      if (rateLimited) {
        // Rate-limited — stop spending calls on the rest of this list rather
        // than 429ing through every remaining address one by one.
        console.warn(`CoinGecko rate-limited after ${i}/${toFetch.length} contract price lookups`);
        break;
      }
      // A rejected/errored request comes back as {timestamp, error_code, status},
      // not a {<address>: {usd: ...}} map — skip it rather than caching a false $0.
      const val = data && data[addr];
      const usd = val ? (val.usd || 0) : 0;
      if (val) {
        result[addr] = usd;
        contractPriceCache[`${platform}:${addr}`] = { usd, fetchedAt: now };
      }
      if (onProgress) onProgress({ stage: 'coingecko', current: i + 1, total: toFetch.length, address: addr, usd: val ? usd : null });
    } catch (e) {
      console.warn(`CoinGecko contract price fetch failed for ${addr}:`, e.message);
      if (onProgress) onProgress({ stage: 'coingecko', current: i + 1, total: toFetch.length, address: addr, usd: null });
    }
    if (i < toFetch.length - 1) await sleep(REQUEST_DELAY_MS);
  }

  return result;
}
